import React from 'react';

import { INK, PerfCard, PerfSplitCard, SectionLabel } from '../report-cards';

import {
  avgVersions,
  avgVersionsWon,
  contactabilityToActive,
  days,
  ghostRatios,
  inr,
  lostTrustability,
  num,
  pct,
  queryToActive,
  ratio,
  ratioHint,
  whatsAppToActive,
} from './band-helpers';

import type { BandProps } from './band-props';

export const PerformanceBand = ({
  periodLabel,
  scopedRow,
  scopedAtv,
  scopedLost,
  perfWon,
  lossDecided,
  lstats,
  poolTotal,
  largeTotal,
  largeMonth,
  largeCarry,
  canSeeTrustability,
  openActiveUnpublished,
}: BandProps): React.JSX.Element => (
  <section className="flex flex-col gap-[10px]">
    <SectionLabel title="Performance" suffix={`${periodLabel} + carry forward`} />
    {/* Row 1 - Conversion ratios. */}
    <div className="grid grid-cols-2 gap-[10px] min-[561px]:grid-cols-5">
      <PerfCard
        label="Leads to contactability"
        dot="#3b82f6"
        valueColor="#1d4ed8"
        value={scopedRow ? pct(scopedRow.contactabilityPct) : '-'}
        hint={
          scopedRow
            ? `${num(scopedRow.contactedCount)} contacted / ${num(scopedRow.totalLeads)} leads`
            : ''
        }
      />
      <PerfCard
        label="Leads to active"
        dot="#3b82f6"
        valueColor="#1d4ed8"
        value={scopedRow ? queryToActive(scopedRow) : '-'}
        hint={
          scopedRow
            ? `${num(scopedRow.reachedActive)} reached active / ${num(scopedRow.totalLeads)}`
            : ''
        }
      />
      <PerfCard
        label="Contactability to active"
        dot="#3b82f6"
        valueColor="#1d4ed8"
        value={scopedRow ? contactabilityToActive(scopedRow) : '-'}
        hint={
          scopedRow
            ? `${num(scopedRow.reachedActive)} / ${num(scopedRow.contactedCount)} contacted`
            : ''
        }
      />
      <PerfCard
        label="WhatsApp to active"
        dot="#22c55e"
        valueColor="#15803d"
        value={scopedRow ? whatsAppToActive(scopedRow) : '-'}
        hint={
          scopedRow
            ? `${num(scopedRow.whatsAppActiveCount)} replied then active / ${num(scopedRow.reachedActive)}`
            : ''
        }
      />
      {canSeeTrustability && (
        <PerfCard
          label="Lost trustability"
          dot="#a1a1aa"
          valueColor={INK}
          value={scopedRow ? lostTrustability(scopedRow) : '-'}
          hint={
            scopedRow
              ? `${num(scopedRow.lostApproved)} approved of ${num(lossDecided)} decided`
              : ''
          }
        />
      )}
    </div>
    {/* Row 3 - Itinerary / quality. */}
    <div className="grid grid-cols-2 gap-[10px] min-[561px]:grid-cols-5">
      <PerfCard
        label="Avg. days to convert"
        dot="#10b981"
        valueColor={INK}
        value={scopedRow ? days(scopedRow.avgConversionDays) : '-'}
        hint="first touch to won"
      />
      <PerfCard
        label="Avg. ticket value"
        dot="#10b981"
        valueColor={INK}
        value={scopedRow && scopedAtv !== null ? inr(scopedAtv) : '-'}
        hint={scopedRow ? `${inr(scopedRow.wonTtvAmount)} won / ${perfWon} files` : ''}
      />
      <PerfCard
        label="Active w/ Itinerary"
        dot="#f59e0b"
        valueColor={INK}
        value={
          scopedRow
            ? `${num(scopedRow.activeItinPublished)}/${num(scopedRow.activeItinTotal)}`
            : '-'
        }
        hint={
          scopedRow
            ? `In progress ${num(scopedRow.activeItinInProgress)} · None ${num(scopedRow.activeItinNone)}`
            : ''
        }
        onClick={
          scopedRow && scopedRow.activeItinNone + scopedRow.activeItinInProgress > 0
            ? openActiveUnpublished
            : undefined
        }
      />
      <PerfCard
        label="Avg itinerary versions"
        dot="#3b82f6"
        valueColor={INK}
        value={scopedRow ? avgVersions(scopedRow) : '-'}
        hint={
          scopedRow
            ? `${num(scopedRow.itineraryVersionsSum)} versions / ${num(scopedRow.itineraryVersionsLeadCount)} leads`
            : ''
        }
      />
      <PerfCard
        label="Avg versions · won"
        dot="#10b981"
        valueColor={INK}
        value={scopedRow ? avgVersionsWon(scopedRow) : '-'}
        hint={
          scopedRow
            ? `${num(scopedRow.wonItineraryVersionsSum)} versions / ${num(scopedRow.wonItineraryVersionsLeadCount)} won`
            : ''
        }
      />
    </div>
    {/* Row 4 - Large leads. */}
    <div className="grid grid-cols-2 gap-[10px] min-[561px]:grid-cols-6">
      <PerfSplitCard
        label="Potential TTV · all leads"
        dot="#10b981"
        className="col-span-2"
        parts={[
          {
            value: scopedRow ? inr(scopedRow.potentialTtvAll) : '-',
            label: 'Potential',
            hint: `across ${num(poolTotal)} leads`,
          },
          {
            value: scopedRow ? inr(scopedRow.receivedTtvAll) : '-',
            valueColor: '#047857',
            label: 'Received',
            hint: `${perfWon} won files`,
          },
        ]}
      />
      <PerfSplitCard
        label="Large leads"
        dot="#f59e0b"
        className="col-span-2"
        parts={[
          {
            value: num(largeTotal),
            label: 'Open',
            hint: `${largeMonth} month + ${largeCarry} carry`,
          },
          {
            value: lstats ? ratio(lstats.wonThisMonth, largeTotal) : '-',
            valueColor: '#b45309',
            label: 'Conversion',
            hint: lstats ? `${lstats.wonThisMonth} won of ${largeTotal}` : '',
          },
          {
            value:
              lstats && lstats.wonThisMonth > 0
                ? inr(lstats.receivedTtv / lstats.wonThisMonth)
                : '-',
            label: 'Avg ticket',
            hint: 'large won files',
          },
        ]}
      />
      <PerfSplitCard
        label="Potential TTV · large leads"
        dot="#f59e0b"
        className="col-span-2"
        parts={[
          {
            value: lstats ? inr(lstats.potentialTtv) : '-',
            label: 'Potential',
            hint: `${largeTotal} open`,
          },
          {
            value: lstats ? inr(lstats.receivedTtv) : '-',
            valueColor: '#047857',
            label: 'Received',
            hint: `${lstats ? lstats.wonThisMonth : 0} won files`,
          },
        ]}
      />
    </div>
    {/* Row 5 - Loss: avg period of loss + ghosting. */}
    <div className="grid grid-cols-2 gap-[10px] min-[561px]:grid-cols-4">
      <PerfSplitCard
        label="Avg. period of loss"
        dot="#ef4444"
        tint
        className="col-span-2"
        parts={[
          {
            value: scopedRow ? days(scopedRow.avgLossDays) : '-',
            valueColor: '#b91c1c',
            label: 'Total',
            hint: `${scopedLost ?? 0} lost`,
          },
          {
            value: scopedRow ? days(scopedRow.avgLossDaysNq) : '-',
            valueColor: '#b91c1c',
            label: 'From NQ',
            hint: 'never contacted',
          },
          {
            value: scopedRow ? days(scopedRow.avgLossDaysNonContact) : '-',
            valueColor: '#b91c1c',
            label: 'From NCL',
            hint: 'no-contact lost',
          },
          {
            value: scopedRow ? days(scopedRow.avgLossDaysActive) : '-',
            valueColor: '#b91c1c',
            label: 'From active',
            hint: 'after proposal',
          },
        ]}
      />
      <PerfSplitCard
        label="Ghosting (Client Unresponsive)"
        dot="#f59e0b"
        tint
        className="col-span-2"
        parts={[
          {
            value: scopedRow ? ghostRatios(scopedRow).proposal.pct : '-',
            valueColor: '#b45309',
            label: 'Proposal',
            hint: scopedRow ? ratioHint(ghostRatios(scopedRow).proposal) : '',
          },
          {
            value: scopedRow ? ghostRatios(scopedRow).warm.pct : '-',
            valueColor: '#b45309',
            label: 'Warm',
            hint: scopedRow ? ratioHint(ghostRatios(scopedRow).warm) : '',
          },
          {
            value: scopedRow ? ghostRatios(scopedRow).hot.pct : '-',
            valueColor: '#b45309',
            label: 'Hot',
            hint: scopedRow ? ratioHint(ghostRatios(scopedRow).hot) : '',
          },
          {
            value: scopedRow ? ghostRatios(scopedRow).total.pct : '-',
            valueColor: '#b45309',
            label: 'Total',
            hint: scopedRow ? ratioHint(ghostRatios(scopedRow).total) : '',
          },
        ]}
      />
    </div>
  </section>
);
